import React, { useRef, useState } from 'react';
import { Modal, Tabs, Button, message } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import type { LiteLocale } from '../pages/lite/i18n';

interface ShareCardProps {
  visible: boolean;
  onClose: () => void;
  locale?: LiteLocale;
  copy: {
    brandEyebrow: string;
    fileName: string;
    saving: string;
    mobileSaved: string;
    saved: string;
    failed: string;
    mobileTitle: string;
    mobileHint: string;
    mobileDownload: string;
    mobileClose: string;
    summaryTab: string;
    posterTab: string;
    summaryTitle: string;
    summaryYearHint: string;
    baseLabel: string;
    yearLabel: string;
    posterYearLabel: string;
    posterTraitsLabel: string;
    posterYearTrendLabel: string;
    saveImage: string;
    qrHint: string;
    footer: string;
  };
  summaryData: {
    gender: string;
    birthInfo: string;
    personalityTags: string[];
    yearKeywords: string[];
    oneLiner: string;
    year: number;
    focusLabel: string;
  };
  siteUrl?: string;
}

const ShareCard: React.FC<ShareCardProps> = ({ visible, onClose, summaryData, siteUrl, locale = 'zh-CN', copy }) => {
  const [saving, setSaving] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const posterRef = useRef<HTMLDivElement>(null);

  const qrUrl = siteUrl || `${window.location.origin}/lite`;

  const handleSave = async (selector: string) => {
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) return;

    setSaving(true);
    try {
      message.loading({ content: copy.saving, key: 'share-save', duration: 0 });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        width: 375,
        windowWidth: 375,
      });

      const imgData = canvas.toDataURL('image/png', 0.95);

      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        showMobilePreview(imgData);
        message.success({ content: copy.mobileSaved, key: 'share-save', duration: 3 });
      } else {
        const link = document.createElement('a');
        link.href = imgData;
        link.download = copy.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success({ content: copy.saved, key: 'share-save', duration: 2 });
      }
    } catch (error) {
      console.error('保存图片失败:', error);
      message.error({ content: copy.failed, key: 'share-save', duration: 2 });
    } finally {
      setSaving(false);
    }
  };

  const showMobilePreview = (imgData: string) => {
    const isAndroid = /Android/i.test(navigator.userAgent);

    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.92); z-index: 9998;
      overflow-y: auto; overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      position: sticky; top: 0; left: 0; right: 0;
      background: linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 70%, transparent 100%);
      padding: 16px 20px 40px; z-index: 10001; text-align: center;
    `;
    header.innerHTML = `
      <div style="color: #ffd700; font-size: 16px; font-weight: 600; margin-bottom: 6px;">
        📱 ${copy.mobileTitle}
      </div>
      <div style="color: rgba(255,255,255,0.7); font-size: 13px;">
        ${copy.mobileHint}
      </div>
    `;

    const imgWrapper = document.createElement('div');
    imgWrapper.style.cssText = `
      padding: 0 16px 100px; display: flex; justify-content: center;
    `;

    const img = new Image();
    img.src = imgData;
    img.style.cssText = `
      max-width: 100%; width: 375px; height: auto;
      box-shadow: 0 10px 40px rgba(0,0,0,0.5); border-radius: 8px;
      background: #1a1a2e; display: block;
    `;
    imgWrapper.appendChild(img);

    const footer = document.createElement('div');
    footer.style.cssText = `
      position: fixed; bottom: 0; left: 0; right: 0;
      background: linear-gradient(0deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 70%, transparent 100%);
      padding: 30px 20px 24px; z-index: 10001;
      display: flex; flex-direction: column; align-items: center; gap: 12px;
    `;

    if (isAndroid) {
      const downloadBtn = document.createElement('button');
      downloadBtn.textContent = copy.mobileDownload;
      downloadBtn.style.cssText = `
        background: linear-gradient(135deg, #d4af6a 0%, #c19b5a 100%);
        color: white; border: none; padding: 14px 40px; border-radius: 25px;
        font-size: 15px; font-weight: 600; cursor: pointer;
        box-shadow: 0 4px 15px rgba(212, 175, 106, 0.4);
        width: 100%; max-width: 280px;
      `;
      downloadBtn.onclick = () => {
        const link = document.createElement('a');
        link.href = imgData;
        link.download = copy.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      footer.appendChild(downloadBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.textContent = copy.mobileClose;
    closeBtn.style.cssText = `
      background: rgba(255, 255, 255, 0.12); color: white;
      border: 1px solid rgba(255, 255, 255, 0.25);
      padding: 12px 32px; border-radius: 20px; font-size: 14px;
      cursor: pointer; width: 100%; max-width: 280px;
    `;
    footer.appendChild(closeBtn);

    const closePreview = () => {
      document.body.removeChild(overlay);
    };
    closeBtn.addEventListener('click', closePreview);

    overlay.appendChild(header);
    overlay.appendChild(imgWrapper);
    overlay.appendChild(footer);
    document.body.appendChild(overlay);
  };

  // 摘要卡片内容
  const summaryCardContent = (
    <div
      style={{
        width: 375,
        padding: '32px 28px',
        background: 'radial-gradient(circle at top, rgba(212, 175, 106, 0.18), transparent 30%), linear-gradient(145deg, #171728 0%, #14203b 52%, #0d3151 100%)',
        borderRadius: 12,
        boxSizing: 'border-box',
        fontFamily: locale === 'zh-CN'
          ? '"PingFang SC", "Noto Serif SC", "Source Han Serif SC", serif'
          : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        border: '1px solid rgba(255, 215, 160, 0.14)',
        boxShadow: '0 18px 48px rgba(8, 12, 28, 0.32)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.05), transparent 28%, transparent 72%, rgba(255,255,255,0.04))',
          pointerEvents: 'none',
        }}
      />
      {/* 顶部标题 */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '5px 12px',
            borderRadius: 999,
            border: '1px solid rgba(255, 215, 160, 0.22)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255, 239, 205, 0.78)',
            fontSize: 11,
            letterSpacing: '0.16em',
            marginBottom: 14,
          }}
        >
          {copy.brandEyebrow}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#ffd700', letterSpacing: 2 }}>
          {copy.summaryTitle}
        </div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
          {locale === 'zh-CN' ? `${summaryData.year} ${copy.summaryYearHint}` : `${copy.summaryYearHint} ${summaryData.year}`}
        </div>
        <div
          style={{
            width: 60,
            height: 2,
            background: 'linear-gradient(90deg, transparent, #ffd700, transparent)',
            margin: '12px auto 0',
          }}
        />
      </div>

      {/* 出生信息 */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: 20,
          padding: '10px 0',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
          {summaryData.gender} · {summaryData.birthInfo}
        </span>
      </div>

      {/* 性格关键词标签 */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10, letterSpacing: 1 }}>
          {copy.baseLabel}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {summaryData.personalityTags.map((tag, idx) => (
            <span
              key={idx}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                background: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: 13,
                backdropFilter: 'blur(4px)',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* 年度运势关键词 */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10, letterSpacing: 1 }}>
          {copy.yearLabel}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {summaryData.yearKeywords.map((keyword, idx) => (
            <span
              key={idx}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                background: 'transparent',
                color: '#ffd700',
                fontSize: 13,
                border: '1px solid rgba(255, 215, 0, 0.5)',
              }}
            >
              {keyword}
            </span>
          ))}
        </div>
      </div>

      {/* 一句话总结 */}
      <div
        style={{
          textAlign: 'center',
          padding: '16px 12px',
          marginBottom: 16,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 10,
        }}
      >
        <div style={{ fontSize: 15, color: '#ffffff', lineHeight: 1.7, fontWeight: 500 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: '8px', letterSpacing: 1 }}>
            {summaryData.focusLabel}
          </div>
          {summaryData.oneLiner}
        </div>
      </div>

      {/* 底部 */}
      <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
        {copy.footer}
      </div>
    </div>
  );

  const tabItems = [
    {
      key: 'summary',
      label: copy.summaryTab,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div ref={summaryRef} className="share-summary-card">
            {summaryCardContent}
          </div>
          <Button
            type="primary"
            loading={saving}
            onClick={() => handleSave('.share-summary-card')}
            style={{
              marginTop: 20,
              background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
              border: 'none',
              fontWeight: 600,
              width: 200,
              height: 44,
            }}
          >
            {copy.saveImage}
          </Button>
        </div>
      ),
    },
    {
      key: 'poster',
      label: copy.posterTab,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div ref={posterRef} className="share-poster-card">
            <div
              style={{
                width: 375,
                background: 'radial-gradient(circle at top, rgba(212, 175, 106, 0.18), transparent 30%), linear-gradient(145deg, #171728 0%, #14203b 52%, #0d3151 100%)',
                borderRadius: 12,
                overflow: 'hidden',
                fontFamily: locale === 'zh-CN'
                  ? '"PingFang SC", "Noto Serif SC", "Source Han Serif SC", serif'
                  : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                border: '1px solid rgba(255, 215, 160, 0.14)',
                boxShadow: '0 18px 48px rgba(8, 12, 28, 0.32)',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.05), transparent 28%, transparent 72%, rgba(255,255,255,0.04))',
                  pointerEvents: 'none',
                }}
              />
              {/* 摘要卡片内容（不含底部圆角） */}
              <div style={{ padding: '32px 28px 20px' }}>
                {/* 顶部标题 */}
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '5px 12px',
                      borderRadius: 999,
                      border: '1px solid rgba(255, 215, 160, 0.22)',
                      background: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255, 239, 205, 0.78)',
                      fontSize: 11,
                      letterSpacing: '0.16em',
                      marginBottom: 14,
                    }}
                  >
                    {copy.brandEyebrow}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#ffd700', letterSpacing: 2 }}>
                    {copy.summaryTitle}
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
                    {locale === 'zh-CN' ? `${summaryData.year}${copy.posterYearLabel}` : `${copy.posterYearLabel} ${summaryData.year}`}
                  </div>
                  <div
                    style={{
                      width: 60,
                      height: 2,
                      background: 'linear-gradient(90deg, transparent, #ffd700, transparent)',
                      margin: '12px auto 0',
                    }}
                  />
                </div>

                {/* 出生信息 */}
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: 20,
                    padding: '10px 0',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
                    {summaryData.gender} · {summaryData.birthInfo}
                  </span>
                </div>

                {/* 性格关键词标签 */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10, letterSpacing: 1 }}>
                    {copy.posterTraitsLabel}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {summaryData.personalityTags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 20,
                          background: 'rgba(255,255,255,0.12)',
                          color: 'rgba(255,255,255,0.9)',
                          fontSize: 13,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 年度运势关键词 */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10, letterSpacing: 1 }}>
                    {locale === 'zh-CN' ? `${summaryData.year} ${copy.posterYearTrendLabel}` : `${copy.posterYearTrendLabel} ${summaryData.year}`}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {summaryData.yearKeywords.map((keyword, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 20,
                          background: 'transparent',
                          color: '#ffd700',
                          fontSize: 13,
                          border: '1px solid rgba(255, 215, 0, 0.5)',
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 一句话总结 */}
                <div
                  style={{
                    textAlign: 'center',
                    padding: '16px 12px',
                    marginBottom: 0,
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 10,
                  }}
                >
                  <div style={{ fontSize: 15, color: '#ffffff', lineHeight: 1.7, fontWeight: 500 }}>
                    "{summaryData.oneLiner}"
                  </div>
                </div>
              </div>

              {/* 海报底部：二维码区域 */}
              <div
                style={{
                  padding: '24px 28px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 16 }}>
                  {copy.qrHint}
                </div>
                <div
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    padding: 12,
                  }}
                >
                  <QRCodeSVG
                    value={qrUrl}
                    size={120}
                    bgColor="transparent"
                    fgColor="#ffffff"
                    level="M"
                  />
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 16 }}>
                  {copy.footer}
                </div>
              </div>
            </div>
          </div>
          <Button
            type="primary"
            loading={saving}
            onClick={() => handleSave('.share-poster-card')}
            style={{
              marginTop: 20,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              fontWeight: 600,
              width: 200,
              height: 44,
            }}
          >
            {copy.saveImage}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={440}
      centered
      destroyOnClose
      title={null}
      styles={{
        body: { padding: '16px 0 0' },
      }}
    >
      <Tabs
        defaultActiveKey="summary"
        items={tabItems}
        centered
        style={{ padding: '0 16px' }}
      />
    </Modal>
  );
};

export default ShareCard;
